/* eslint-disable react/jsx-no-bind */
/* eslint-disable no-nested-ternary */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Skeleton } from "@mui/material";
import { IoSearch } from "react-icons/io5";
import {
  FiMessageSquare,
  FiSend,
  FiClock,
  FiMoreVertical,
  FiLogOut,
} from "react-icons/fi";
import NavCommon from "components/@navHeader";
import { getCurrentToken } from "hooks/useRefreshToken";
import { useContatosWA } from "services/api/AppWeb_WhatsApp/useWhatsAppContatos";
import { useWhatsAppHub } from "services/api/AppWeb_WhatsApp/useWhatsAppHub";
import { handleWhatsAppHubPayload } from "services/api/AppWeb_WhatsApp/whatsAppHubEvents";
import {
  useWhatsAppStatus,
  useWhatsAppDisconnect,
} from "services/api/AppWeb_WhatsApp/useWhatsAppStatus";
import {
  markWhatsAppContactRead,
  useWhatsAppHistorico,
  useSaldoEnvioEmMassa,
} from "services/api/AppWeb_WhatsApp/useWhatsAppConversas";
import {
  ContatoWA,
  ConvResumo,
  WhatsAppLead,
  WhatsAppHistoryFilters,
} from "services/api/AppWeb_WhatsApp/types";
import PainelChat from "./components/PainelChat";
import WhatsAppConnect from "./components/WhatsAppConnect";
import ContatoItem from "./components/ContatoItem";
import EnvioMassaPanel from "./components/EnvioMassaPanel";
import HistoricoEnviosPanel from "./components/HistoricoEnviosPanel";
import { WhatsAppTab, contatoId, contatoRowId } from "./types";
import * as S from "./styles";

function parsePtBrDateToSortValue(value: string | undefined): number {
  const text = String(value || "").trim();
  if (!text) return 0;

  const match = text.match(
    /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/
  );

  if (match) {
    const [, dd, mm, yyyy, hh = "00", min = "00", ss = "00"] = match;
    return new Date(`${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}`).getTime();
  }

  return new Date(text).getTime() || 0;
}

const WhatsAppScreen: React.FC = () => {
  const history = useHistory();
  const { leadId } = useParams<{ leadId?: string }>();

  const [tab, setTab] = useState<WhatsAppTab>("conversas");
  const [search, setSearch] = useState("");
  const [connected, setConnected] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [timeReference, setTimeReference] = useState(Date.now());
  const [historyFilters, setHistoryFilters] = useState<WhatsAppHistoryFilters>(
    {}
  );
  const queryClient = useQueryClient();
  const [showDisconnect, setShowDisconnect] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const { data: statusData } = useWhatsAppStatus();
  const { data: contatos = [], isLoading: contatosLoading } = useContatosWA();
  const isConnected = statusData?.data?.conectado === true || connected;
  const shouldFetchHistorico = tab === "historico" && isConnected;
  const { data: apiHistoryRows = [], isFetching: historyLoading } =
    useWhatsAppHistorico(shouldFetchHistorico, historyFilters);
  const { data: saldoEnvio } = useSaldoEnvioEmMassa(isConnected);
  const disconnect = useWhatsAppDisconnect();

  const handleStatusChange = useCallback((state: boolean) => {
    setConnected(state);
  }, []);

  const handleHubMensagem = useCallback(
    (payload: any) => {
      handleWhatsAppHubPayload({
        queryClient,
        payload,
        activeLeadId: leadId,
        onConnectionStatus: handleStatusChange,
      });
    },
    [handleStatusChange, leadId, queryClient]
  );

  useWhatsAppHub(getCurrentToken() || "", handleHubMensagem);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setTimeReference(Date.now());
    }, 15000);

    return () => window.clearInterval(timer);
  }, []);

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    if (!menuOpen) return undefined;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpen]);

  const contatosAsLeads: WhatsAppLead[] = useMemo(
    () =>
      contatos.map((c) => ({
        id: contatoId(c),
        corretorId: c.corretorId,
        nome: c.nome || c.telefone,
        telefone: c.telefone,
        fotoUrl: c.fotoUrl,
        empreendimento: "",
        origem: "whatsapp",
        status: "ativo",
        dataCriacao: c.primeiraMensagem || "",
      })),
    [contatos]
  );

  const contatosResumos: ConvResumo[] = useMemo(
    () =>
      contatos.map((c) => {
        const id = contatoId(c);
        return {
          leadId: id,
          ultimaMensagem: c.ultimaMensagem || "",
          direcao: c.ultimaMensagemFromMe
            ? ("saida" as const)
            : ("entrada" as const),
          data: c.ultimaAtividade || "",
          naoLidas: c.unreadCount || 0,
        };
      }),
    [contatos]
  );

  const leads = contatosAsLeads;
  const allResumos = contatosResumos;
  const leadAtivo = leads.find((l) => l.id === leadId);

  useEffect(() => {
    if (!leadId) return;
    const resumo = allResumos.find((r) => r.leadId === leadId);
    markWhatsAppContactRead(queryClient, leadId, resumo?.data);
  }, [allResumos, leadId, queryClient]);

  const totalUnread = useMemo(
    () => allResumos.reduce((sum, r) => sum + (r.naoLidas || 0), 0),
    [allResumos]
  );

  const filtered = useMemo(
    () =>
      leads
        .filter(
          (l) =>
            (l.nome || "").toLowerCase().includes(search.toLowerCase()) ||
            (l.telefone || "").includes(search)
        )
        .sort((a, b) => {
          const ca = contatos.find((c) => contatoId(c) === a.id);
          const cb = contatos.find((c) => contatoId(c) === b.id);

          if (ca && cb) {
            return (
              new Date(cb.ultimaAtividade).getTime() -
              new Date(ca.ultimaAtividade).getTime()
            );
          }

          const ra = allResumos.find((r) => r.leadId === a.id);
          const rb = allResumos.find((r) => r.leadId === b.id);

          if (!ra && !rb) return 0;
          if (!ra) return 1;
          if (!rb) return -1;

          return new Date(rb.data).getTime() - new Date(ra.data).getTime();
        }),
    [allResumos, contatos, leads, search]
  );

  const historyData = useMemo(() => {
    return [...apiHistoryRows].sort(
      (a, b) =>
        parsePtBrDateToSortValue(b.dataHoraSort || b.dataHora) -
        parsePtBrDateToSortValue(a.dataHoraSort || a.dataHora)
    );
  }, [apiHistoryRows]);

  const handleTabChange = useCallback(
    (nextTab: WhatsAppTab) => {
      setTab(nextTab);
      if (nextTab !== "conversas" && leadId) {
        history.push("/whatsapp");
      }
    },
    [history, leadId]
  );

  function handleDisconnect() {
    disconnect.mutate(undefined, {
      onSuccess: () => {
        setShowDisconnect(false);
        setConnected(false);
      },
    });
  }

  if (!isConnected) {
    return (
      <>
        <NavCommon
          breadcrumbs={[{ path: "", name: "WhatsApp" }]}
          onBack={() => history.goBack()}
          onChangeActionNew={() => history.push("/menu")}
        />
        <S.Content>
          <S.ConnectWrapper>
            <WhatsAppConnect onStatusChange={handleStatusChange} />
          </S.ConnectWrapper>
        </S.Content>
      </>
    );
  }

  return (
    <>
      <NavCommon
        breadcrumbs={[{ path: "", name: "WhatsApp" }]}
        onBack={() => history.goBack()}
        onChangeActionNew={() => history.push("/menu")}
      />
      <S.Content>
        <S.TopTabsBar>
          <S.TopTabsGroup>
            <S.TopTabButton
              active={tab === "conversas"}
              onClick={() => handleTabChange("conversas")}
              type="button"
            >
              <FiMessageSquare size={14} /> Conversas
              {totalUnread > 0 && (
                <S.FloatingBadge>
                  {totalUnread > 99 ? "99+" : totalUnread}
                </S.FloatingBadge>
              )}
            </S.TopTabButton>
            <S.TopTabButton
              active={tab === "massa"}
              onClick={() => handleTabChange("massa")}
              type="button"
            >
              <FiSend size={14} /> Envio em Massa
            </S.TopTabButton>
            <S.TopTabButton
              active={tab === "historico"}
              onClick={() => handleTabChange("historico")}
              type="button"
            >
              <FiClock size={14} /> Histórico de Envios
            </S.TopTabButton>
          </S.TopTabsGroup>

          {saldoEnvio && (
            <S.EnviosHojePill>
              <S.EnviosHojeDot />
              <S.EnviosHojeValue>{saldoEnvio.utilizado}</S.EnviosHojeValue>
              <S.EnviosHojeMuted>
                / {saldoEnvio.limite} envios hoje
              </S.EnviosHojeMuted>
            </S.EnviosHojePill>
          )}
        </S.TopTabsBar>

        {tab === "conversas" && (
          <S.MainContainer>
            <S.Sidebar hidden={!!leadAtivo}>
              <S.SidebarHeader>
                <S.SidebarTopRow>
                  <S.SidebarTitle>Conversas</S.SidebarTitle>
                  <div ref={menuRef} style={{ position: "relative" }}>
                    <S.SidebarMenuBtn
                      type="button"
                      onClick={() => setMenuOpen((v) => !v)}
                    >
                      <FiMoreVertical size={18} />
                    </S.SidebarMenuBtn>
                    {menuOpen && (
                      <S.SidebarDropdown>
                        <S.SidebarDropdownItem
                          type="button"
                          danger
                          onClick={() => {
                            setMenuOpen(false);
                            setShowDisconnect(true);
                          }}
                        >
                          <FiLogOut size={16} />
                          Sair da sessão
                        </S.SidebarDropdownItem>
                      </S.SidebarDropdown>
                    )}
                  </div>
                </S.SidebarTopRow>

                <S.SearchWrapper>
                  <IoSearch size={15} color="#54656f" />
                  <S.SearchInput
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Pesquisar ou começar uma nova conversa"
                  />
                  {search && (
                    <S.ClearButton onClick={() => setSearch("")}>
                      ×
                    </S.ClearButton>
                  )}
                </S.SearchWrapper>
              </S.SidebarHeader>

              <S.ContactList>
                {contatosLoading && (
                  <div style={{ padding: 16 }}>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          gap: 13,
                          alignItems: "center",
                          padding: "8px 0",
                        }}
                      >
                        <Skeleton variant="circular" width={48} height={48} />
                        <div style={{ flex: 1 }}>
                          <Skeleton variant="text" width="55%" height={18} />
                          <Skeleton variant="text" width="75%" height={14} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {!contatosLoading && filtered.length === 0 && (
                  <S.EmptyState>
                    <S.EmptyStateTitle>
                      Nenhum contato encontrado
                    </S.EmptyStateTitle>
                    <S.EmptyStateSubtitle>
                      Ajuste a busca para encontrar uma conversa.
                    </S.EmptyStateSubtitle>
                  </S.EmptyState>
                )}

                {!contatosLoading &&
                  filtered.map((item) => {
                    // Vários contatos podem compartilhar o mesmo leadCorretorId
                    // (lead com vários telefones). Usamos rowKey do contato
                    // original para diferenciar a React key.
                    const original = contatos.find(
                      (c) =>
                        contatoId(c) === item.id && c.telefone === item.telefone
                    );
                    const renderKey = contatoRowId(
                      original ?? ({ leadCorretorId: item.id } as ContatoWA)
                    );
                    return (
                      <ContatoItem
                        key={renderKey}
                        lead={item}
                        resumo={allResumos.find((r) => r.leadId === item.id)}
                        ativo={item.id === leadId}
                        timeReference={timeReference}
                        onClick={() => {
                          const resumo = allResumos.find(
                            (r) => r.leadId === item.id
                          );
                          markWhatsAppContactRead(
                            queryClient,
                            item.id,
                            resumo?.data
                          );
                          history.push(`/whatsapp/${item.id}`);
                        }}
                      />
                    );
                  })}
              </S.ContactList>
            </S.Sidebar>

            {leadAtivo ? (
              <PainelChat
                key={leadAtivo.id}
                lead={leadAtivo}
                onBack={() => history.push("/whatsapp")}
                onSent={() => {}}
              />
            ) : (
              <S.EmptyPanel>
                <S.EmptyPanelIcon>
                  <FiMessageSquare size={34} />
                </S.EmptyPanelIcon>
                <S.EmptyPanelText>
                  Selecione uma conversa para começar
                </S.EmptyPanelText>
                <S.EmptyPanelSub>
                  {filtered.length} contato{filtered.length !== 1 ? "s" : ""}{" "}
                  {filtered.length !== 1 ? "encontrados" : "encontrado"}
                </S.EmptyPanelSub>
              </S.EmptyPanel>
            )}
          </S.MainContainer>
        )}

        {tab === "massa" && (
          <S.PanelCard>
            <EnvioMassaPanel onHistoryAppend={() => {}} />
          </S.PanelCard>
        )}

        {tab === "historico" && (
          <S.PanelCard>
            <HistoricoEnviosPanel
              rows={historyData}
              loading={historyLoading}
              filters={historyFilters}
              onFiltersChange={setHistoryFilters}
            />
          </S.PanelCard>
        )}
      </S.Content>

      {showDisconnect && (
        <S.ConfirmOverlay onClick={() => setShowDisconnect(false)}>
          <S.ConfirmCard
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <S.ConfirmBody>
              <S.ConfirmTitle>Sair da sessão?</S.ConfirmTitle>
              <S.ConfirmText>
                Sua conta será desconectada do WhatsApp Web. Você precisará
                escanear o QR Code novamente para reconectar.
              </S.ConfirmText>
            </S.ConfirmBody>
            <S.ConfirmActions>
              <S.ConfirmBtnCancel
                type="button"
                onClick={() => setShowDisconnect(false)}
              >
                Cancelar
              </S.ConfirmBtnCancel>
              <S.ConfirmBtnDanger
                type="button"
                onClick={handleDisconnect}
                disabled={disconnect.isLoading}
              >
                {disconnect.isLoading ? "Saindo..." : "Sair"}
              </S.ConfirmBtnDanger>
            </S.ConfirmActions>
          </S.ConfirmCard>
        </S.ConfirmOverlay>
      )}
    </>
  );
};

export default WhatsAppScreen;
